import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Users } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { useWorld } from "@/context/WorldContext";
import { TeamMembers } from "@/components/TeamMembers";
import { TeamInvites } from "@/components/TeamInvites";
import { TransferOwnership } from "@/components/TeamOwnership";

const ReviewSetting = ({ team, worldId, reload }) => {
  const owner = team.my_role === "owner";
  const flip = async (v) => {
    try { await api.put(`/worlds/${worldId}/settings`, { review_required: v }); toast.success(v ? "Editor changes now need approval" : "Editor changes go live straight away"); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="rounded-xl border border-line p-4 flex items-start justify-between gap-4" data-testid="review-setting">
      <div>
        <p className="text-sm text-slate-200">Changes need approval</p>
        <p className="text-[11px] text-slate-500 mt-1">Edits, new entries and deletions by Editors wait in Pending changes until you or an admin accepts them. Suggestions always need review.{!owner && " Only the owner can change this."}</p>
      </div>
      <Switch checked={team.review_required} disabled={!owner} onCheckedChange={flip} data-testid="review-required-switch" />
    </div>
  );
};

export const TeamPanel = () => {
  const { world, pages, reload: reloadWorld } = useWorld();
  const [team, setTeam] = useState(null);
  const load = useCallback(() => api.get(`/worlds/${world.id}/team`).then((r) => setTeam(r.data)).catch((e) => toast.error(formatApiError(e))), [world.id]);
  const reload = () => { load(); reloadWorld(); };
  const props = { team, worldId: world.id, pages, reload };
  const [sp, setSp] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("members");
  const toggle = (o) => { setOpen(o); if (o) load(); };
  useEffect(() => {
    if (!sp.get("team")) return;
    setTab(sp.get("team") === "requests" ? "invites" : "members");
    toggle(true);
    sp.delete("team");
    setSp(sp, { replace: true });
  }, [sp]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Sheet open={open} onOpenChange={toggle}>
      <SheetTrigger asChild>
        <button className="inline-flex items-center gap-1.5 text-xs rounded-full border border-line px-3 py-1.5 text-slate-300 hover:border-amber-500/40 hover:text-amber-300 transition-colors" data-testid="team-btn">
          <Users className="w-3.5 h-3.5" /><span className="hidden sm:inline">Team</span>
        </button>
      </SheetTrigger>
      <SheetContent className="bg-panel border-line w-full sm:max-w-lg overflow-y-auto" data-testid="team-panel">
        <SheetTitle className="font-display text-3xl text-amber-100">Team</SheetTitle>
        <SheetDescription className="text-slate-400 text-sm">Invite writers and decide who edits what.</SheetDescription>
        {!world.owner_pro && <p className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">Collaboration needs the owner to be on Pro. Members can still read.</p>}
        {team && (
          <Tabs value={tab} onValueChange={setTab} className="mt-5">
            <TabsList className="bg-inset">
              <TabsTrigger value="members" data-testid="team-tab-members">Members ({team.members.length + 1})</TabsTrigger>
              <TabsTrigger value="invites" data-testid="team-tab-invites">Invites{team.requests?.length ? <span className="ml-1.5 rounded-full bg-amber-500 text-onaccent text-[10px] font-bold px-1.5" data-testid="join-requests-count">{team.requests.length}</span> : null}</TabsTrigger>
              <TabsTrigger value="settings" data-testid="team-tab-settings">Settings</TabsTrigger>
            </TabsList>
            <TabsContent value="members"><TeamMembers {...props} /></TabsContent>
            <TabsContent value="invites"><TeamInvites {...props} /></TabsContent>
            <TabsContent value="settings" className="space-y-4"><ReviewSetting {...props} /><TransferOwnership {...props} /></TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
};
